import { create } from 'zustand';
import { EstateItem, Classification, EstateStageJson } from '@types';
import { Point } from '@types';

export interface EstateMetadata {
  planNumber: string;
  name: string;
  stage: string;
  stages?: EstateStageJson[];
  address: string;
  status: string;
  geolocation: Point;
}

type JobStatus =
  | 'idle'
  | 'submitting'
  | 'queued'
  | 'running'
  | 'downloading'
  | 'completed'
  | 'failed';

interface EstateStore {
  metadata: EstateMetadata | null;
  originalMetadata: EstateMetadata | null;
  items: EstateItem[];
  originalItems: EstateItem[];
  selectedCode: string | null;

  // Job tracking
  jobId: string | null;
  jobStatus: JobStatus;
  jobMessage: string;
  jobError: string | null;

  // Actions
  loadEstate: (metadata: EstateMetadata, lots: Omit<EstateItem, 'classification'>[]) => void;
  loadEstateWithItems: (metadata: EstateMetadata, items: EstateItem[]) => void;
  classify: (code: string, classification: Classification) => void;
  updateLotStatus: (code: string, status: string) => void;
  updateLotPrice: (code: string, price: number) => void;
  bulkAdjustPrice: (amount: number) => void;
  importPricing: (lots: { code: string; price: number }[]) => number;
  updateStages: (stages: EstateStageJson[]) => void;
  setSelected: (code: string | null) => void;
  translateEstate: (delta: Point) => void;
  reset: () => void;

  // Job actions
  setJobState: (
    state: Partial<{
      jobId: string | null;
      jobStatus: JobStatus;
      jobMessage: string;
      jobError: string | null;
    }>
  ) => void;
  clearJob: () => void;

  // Derived getters
  getLots: () => EstateItem[];
  getReferences: () => EstateItem[];
  getDeletedCount: () => number;
  getSelected: () => EstateItem | undefined;
}

export const useEstateStore = create<EstateStore>((set, get) => ({
  metadata: null,
  originalMetadata: null,
  items: [],
  originalItems: [],
  selectedCode: null,

  jobId: null,
  jobStatus: 'idle',
  jobMessage: '',
  jobError: null,

  loadEstate: (metadata, lots) => {
    const items = lots.map((lot) => ({
      ...lot,
      classification: 'lot' as Classification,
    }));
    set({
      metadata,
      originalMetadata: metadata,
      items,
      originalItems: items,
      selectedCode: null,
      jobStatus: 'completed',
      jobMessage: 'Estate loaded',
      jobError: null,
    });
  },

  loadEstateWithItems: (metadata, items) => {
    set({
      metadata,
      originalMetadata: metadata,
      items,
      originalItems: items,
      selectedCode: null,
      jobStatus: 'completed',
      jobMessage: 'Estate loaded from file',
      jobError: null,
    });
  },

  classify: (code, classification) => {
    set((state) => {
      const stages =
        classification !== 'lot'
          ? state.metadata?.stages?.map((stage) => ({
              ...stage,
              lots: (stage.lots ?? []).map(String).filter((lotCode) => lotCode !== code),
            }))
          : state.metadata?.stages;

      return {
        items: state.items.map((item) => (item.code === code ? { ...item, classification } : item)),
        metadata:
          state.metadata && stages
            ? {
                ...state.metadata,
                stage: stages
                  .map((stage) => stage.name.trim())
                  .filter(Boolean)
                  .join(', '),
                stages,
              }
            : state.metadata,
      };
    });
  },

  updateLotStatus: (code, status) => {
    set((state) => ({
      items: state.items.map((item) => (item.code === code ? { ...item, status } : item)),
    }));
  },

  updateLotPrice: (code, price) => {
    set((state) => ({
      items: state.items.map((item) => (item.code === code ? { ...item, price } : item)),
    }));
  },

  bulkAdjustPrice: (amount) => {
    set((state) => ({
      items: state.items.map((item) =>
        item.classification === 'lot' && item.status !== 'sold'
          ? { ...item, price: item.price + amount }
          : item
      ),
    }));
  },

  importPricing: (lots) => {
    const priceMap = new Map(lots.map((l) => [l.code, l.price]));
    let matched = 0;
    set((state) => ({
      items: state.items.map((item) => {
        if (item.classification === 'lot' && priceMap.has(item.code)) {
          matched++;
          return { ...item, price: priceMap.get(item.code)! };
        }
        return item;
      }),
    }));
    return matched;
  },

  updateStages: (stages) => {
    const copiedStages = stages.map((stage) => ({
      ...stage,
      lots: (stage.lots ?? []).map(String),
    }));
    set((state) => ({
      metadata: state.metadata
        ? {
            ...state.metadata,
            stage: copiedStages
              .map((stage) => stage.name.trim())
              .filter(Boolean)
              .join(', '),
            stages: copiedStages,
          }
        : null,
    }));
  },

  setSelected: (code) => {
    set({ selectedCode: code });
  },

  translateEstate: (delta) => {
    set((state) => ({
      metadata: state.metadata
        ? {
            ...state.metadata,
            geolocation: {
              lat: state.metadata.geolocation.lat + delta.lat,
              lng: state.metadata.geolocation.lng + delta.lng,
            },
          }
        : null,
      items: state.items.map((item) => ({
        ...item,
        points: item.points.map((point) => ({
          lat: point.lat + delta.lat,
          lng: point.lng + delta.lng,
        })),
      })),
      selectedCode: null,
    }));
  },

  reset: () => {
    set((state) => ({
      metadata: state.originalMetadata,
      items: state.originalItems,
      selectedCode: null,
    }));
  },

  setJobState: (state) => {
    set(state);
  },

  clearJob: () => {
    set({
      metadata: null,
      originalMetadata: null,
      items: [],
      originalItems: [],
      selectedCode: null,
      jobId: null,
      jobStatus: 'idle',
      jobMessage: '',
      jobError: null,
    });
  },

  getLots: () => get().items.filter((i) => i.classification === 'lot'),

  getReferences: () =>
    get().items.filter((i) => i.classification !== 'lot' && i.classification !== 'deleted'),

  getDeletedCount: () => get().items.filter((i) => i.classification === 'deleted').length,

  getSelected: () => {
    const { items, selectedCode } = get();
    return items.find((i) => i.code === selectedCode);
  },
}));
